import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { TUTORIAL_CHAPTERS } from '@il-colpo/engine';
import { useGameStore } from '../../game/gameStore.ts';
import { useLooseT } from '../../game/text/loose.ts';
import { tutorialSetup } from '../../game/tutorial/setup.ts';
import { useProgressStore } from '../../state/progressStore.ts';
import { useSettings } from '../../state/settingsStore.ts';
import { Badge, Button, Panel, ScreenFrame } from '../../ui/index.ts';
import styles from './TutorialMenu.module.css';

/** The Tutorial (GDD §11): five chapters, one role each; any of them can be played first. */
export function TutorialMenu() {
  const { t } = useTranslation('tutorial');
  const { t: ts } = useTranslation('setup');
  const g = useLooseT('game');
  const tl = useLooseT('tutorial');
  const navigate = useNavigate();
  const { language } = useSettings();
  const start = useGameStore((s) => s.start);
  const done = useProgressStore((s) => s.progress.tutorial);
  const [starting, setStarting] = useState<number | null>(null);

  const go = async (chapter: 1 | 2 | 3 | 4 | 5) => {
    setStarting(chapter);
    await start(tutorialSetup(chapter, ts('you'), language));
    void navigate('/game');
  };

  return (
    <ScreenFrame title={t('title')} backTo="/play" tone="blueprint">
      <div className={styles['list']}>
        <p className={styles['intro']}>{t('intro')}</p>
        <ol className={styles['chapters']}>
          {TUTORIAL_CHAPTERS.map((c) => {
            const finished = done.includes(c.id);
            return (
              <li key={c.id}>
                <Panel
                  tone="blueprint"
                  as="article"
                  className={styles['chapter']}
                  aria-label={tl(`chapters.c${c.id}.title`)}
                >
                  <div className={styles['head']}>
                    <span className={styles['number']}>{t('chapter', { n: c.id })}</span>
                    <Badge tone="cyan">{g(`roles.${c.role}`)}</Badge>
                    {finished ? <Badge tone="green">{t('done')}</Badge> : null}
                  </div>
                  <h2 className={styles['title']}>{tl(`chapters.c${c.id}.title`)}</h2>
                  <p className={styles['lesson']}>{tl(`chapters.c${c.id}.lesson`)}</p>
                  <Button
                    variant={finished ? 'secondary' : 'primary'}
                    icon="play"
                    loading={starting === c.id}
                    onClick={() => void go(c.id)}
                  >
                    {finished ? t('replay') : t('start')}
                  </Button>
                </Panel>
              </li>
            );
          })}
        </ol>
      </div>
    </ScreenFrame>
  );
}
